import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import { ViewportCursorStyle } from '../../viewer/moudles/ViewportCursorManager';
import { PatternDirectionArrowObject } from './PatternDirectionArrowObject';
import {
    PatternDirectionArrowAxisDragPhase,
    type PatternDirectionArrowAxisDragEvent,
} from './PatternDirectionArrowAxisDragTypes';


/**
 * 方向箭头轴向拖拽控制器输入。
 */
export interface PatternDirectionArrowAxisDragControllerInput {
    /** Viewer 实例。 */
    viewer: Viewer;
    /** 拖拽拾取根对象。 */
    root: THREE.Object3D;
    /** 拖拽事件回调。 */
    onAxisDrag: (event: PatternDirectionArrowAxisDragEvent) => void;
}

/**
 * 方向箭头轴向拖拽会话。
 */
interface PatternDirectionArrowAxisDragSession {
    /** 当前拖拽的箭头对象。 */
    arrow: PatternDirectionArrowObject;
    /** 拖拽上下文标识。 */
    contextKey: string;
    /** 拖拽开始时的业务标量值。 */
    startScalarValue: number;
    /** 场景单位到业务标量单位的换算比例。 */
    sceneUnitToScalarScale: number;
    /** 轴原点。 */
    axisOriginWorld: THREE.Vector3;
    /** 轴方向。 */
    axisDirectionWorld: THREE.Vector3;
    /** 拖拽开始时鼠标投影到轴上的场景距离。 */
    startProjectedScene: number;
    /** 最近一次派发的场景增量。 */
    lastDeltaScene: number;
    /** 最近一次派发的业务标量值。 */
    lastScalarValue: number;
    /** 最近一次拖拽事件的客户端 X 坐标，供窗口取消事件完成 End 派发。 */
    lastClientX: number;
    /** 最近一次拖拽事件的客户端 Y 坐标，供窗口取消事件完成 End 派发。 */
    lastClientY: number;
}

/**
 * 方向箭头轴向拖拽控制器。
 *
 * 设计说明：
 * 1. 控制器只负责将鼠标事件转换为轴向拖拽事件，不修改任何业务参数；
 * 2. 拾取对象必须是 `PatternDirectionArrowObject` 或其子对象，避免影响普通场景选择；
 * 3. 拖拽中即便预览重建旧箭头被移除，也继续使用会话快照计算增量，保证拖拽连续。
 * 4. 使用容器捕获阶段监听先于 EventManager 消费命中箭头的事件，避免实体选择先启动框选。
 */
export class PatternDirectionArrowAxisDragController {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 拾取根对象。 */
    private readonly root: THREE.Object3D;

    /** 拖拽事件回调。 */
    private readonly onAxisDrag: (event: PatternDirectionArrowAxisDragEvent) => void;

    /** 鼠标射线拾取器。 */
    private readonly raycaster = new THREE.Raycaster();

    /** 鼠标标准设备坐标。 */
    private readonly mouseNdc = new THREE.Vector2();

    /** 当前绑定的 Viewer 容器，用于对称移除捕获阶段监听。 */
    private boundContainer: HTMLElement | null = null;

    /** 当前拖拽会话。 */
    private dragSession: PatternDirectionArrowAxisDragSession | null = null;

    /** 当前是否已经压入可拖拽光标。 */
    private hasGrabCursor = false;

    /** 当前是否已经压入拖拽中光标。 */
    private hasGrabbingCursor = false;

    /** 是否需要吞掉当前箭头交互结束后由浏览器合成的 click。 */
    private shouldSuppressNextClick = false;

    /** 是否已经绑定事件流。 */
    private isBound = false;

    /**
     * 创建方向箭头轴向拖拽控制器。
     *
     * @param input 构造输入。
     */
    public constructor(input: PatternDirectionArrowAxisDragControllerInput) {
        this.viewer = input.viewer;
        this.root = input.root;
        this.onAxisDrag = input.onAxisDrag;
    }

    /**
     * 绑定 Viewer 容器捕获阶段事件。
     *
     * 捕获阶段必须早于 EventManager 的容器冒泡监听执行，否则实体选择会先记录框选起点，
     * 后续再调用 `preventDefault()` 已无法撤销同一事件流中已经发生的状态写入。
     */
    public bind(): void {
        if (this.isBound) {
            return;
        }

        const container = this.viewer.getContainer?.();
        if (!container) {
            return;
        }

        this.boundContainer = container;
        container.addEventListener('mousedown', this.handleMouseDown, true);
        container.addEventListener('mousemove', this.handleMouseMove, true);
        container.addEventListener('mouseup', this.handleMouseUp, true);
        container.addEventListener('click', this.handleClick, true);

        // 鼠标可能在画布外释放或被系统取消，窗口级监听保证拖拽会话和光标一定收口。
        window.addEventListener('mouseup', this.handleWindowMouseUp, true);
        window.addEventListener('pointercancel', this.handleWindowPointerCancel, true);
        window.addEventListener('blur', this.handleWindowBlur, true);
        this.isBound = true;
    }

    /**
     * 释放控制器。
     */
    public dispose(): void {
        this.dragSession = null;
        this.shouldSuppressNextClick = false;
        this.clearCursor();

        if (this.boundContainer) {
            this.boundContainer.removeEventListener('mousedown', this.handleMouseDown, true);
            this.boundContainer.removeEventListener('mousemove', this.handleMouseMove, true);
            this.boundContainer.removeEventListener('mouseup', this.handleMouseUp, true);
            this.boundContainer.removeEventListener('click', this.handleClick, true);
            this.boundContainer = null;
        }
        window.removeEventListener('mouseup', this.handleWindowMouseUp, true);
        window.removeEventListener('pointercancel', this.handleWindowPointerCancel, true);
        window.removeEventListener('blur', this.handleWindowBlur, true);
        this.isBound = false;
    }

    /**
     * 处理鼠标按下。
     *
     * @param event 鼠标事件。
     */
    private readonly handleMouseDown = (event: MouseEvent): void => {
        if (event.button !== 0) {
            return;
        }
        const arrow = this.resolvePickedArrow(event);
        if (!arrow) {
            // 若上一次拖拽未产生 click，新一轮空白交互不应被过期抑制标记影响。
            this.shouldSuppressNextClick = false;
            return;
        }

        // 箭头已命中就必须立即认领本次交互；即使当前视角无法建立轴向投影，也不能让实体框选获得 mousedown。
        this.shouldSuppressNextClick = true;
        this.consumeEvent(event);
        const dragState = arrow.getAxisDragState();
        const frame = arrow.getAxisDragFrame();
        const axisDirection = this.normalizeAxis(frame.directionWorld);
        const pointOnAxis = this.resolvePointOnAxis(event, frame.anchorWorld, axisDirection);
        if (!pointOnAxis) {
            return;
        }

        // 将拖拽起点投影保存为标量，后续移动只需要比较投影差值。
        const startProjectedScene = pointOnAxis.clone().sub(frame.anchorWorld).dot(axisDirection);
        this.dragSession = {
            arrow,
            contextKey: dragState.contextKey,
            startScalarValue: dragState.scalarValue,
            sceneUnitToScalarScale: dragState.sceneUnitToScalarScale,
            axisOriginWorld: frame.anchorWorld,
            axisDirectionWorld: axisDirection,
            startProjectedScene,
            lastDeltaScene: 0,
            lastScalarValue: dragState.scalarValue,
            lastClientX: event.clientX,
            lastClientY: event.clientY,
        };

        this.emitDragEvent(PatternDirectionArrowAxisDragPhase.Start, event.clientX, event.clientY, 0);
        this.setDraggingCursor();
    };

    /**
     * 处理鼠标移动。
     *
     * @param event 鼠标事件。
     */
    private readonly handleMouseMove = (event: MouseEvent): void => {
        if (!this.dragSession) {
            this.updateHoverCursor(event);
            return;
        }

        // 活动拖拽的每个移动事件都必须在捕获阶段被消费，即便本帧射线无法投影到轴上。
        this.consumeEvent(event);
        const pointOnAxis = this.resolvePointOnAxis(
            event,
            this.dragSession.axisOriginWorld,
            this.dragSession.axisDirectionWorld,
        );
        if (!pointOnAxis) {
            return;
        }

        // 当前投影与起点投影的差值就是沿箭头轴方向的场景单位拖拽量。
        const currentProjectedScene = pointOnAxis
            .clone()
            .sub(this.dragSession.axisOriginWorld)
            .dot(this.dragSession.axisDirectionWorld);
        const deltaScene = currentProjectedScene - this.dragSession.startProjectedScene;
        this.emitDragEvent(
            PatternDirectionArrowAxisDragPhase.Change,
            event.clientX,
            event.clientY,
            deltaScene,
        );
    };

    /**
     * 处理鼠标抬起。
     *
     * @param event 鼠标事件。
     */
    private readonly handleMouseUp = (event: MouseEvent): void => {
        if (!this.dragSession) {
            return;
        }
        this.finishDragSession(event.clientX, event.clientY, true);
        this.consumeEvent(event);
    };

    /**
     * 处理窗口级鼠标释放。
     *
     * 窗口捕获阶段早于容器捕获阶段；活动拖拽存在时在此直接结束并消费，
     * 既覆盖画布外释放，也避免同一 mouseup 继续进入 EventManager。
     *
     * @param event 窗口鼠标释放事件。
     */
    private readonly handleWindowMouseUp = (event: MouseEvent): void => {
        if (!this.dragSession) {
            return;
        }
        this.finishDragSession(event.clientX, event.clientY, true);
        this.consumeWindowPointerEvent(event);
    };

    /**
     * 处理窗口级指针取消。
     *
     * @param event 指针取消事件。
     */
    private readonly handleWindowPointerCancel = (event: Event): void => {
        if (!this.dragSession) {
            return;
        }
        this.finishDragSession(
            this.dragSession.lastClientX,
            this.dragSession.lastClientY,
            false,
        );
        this.consumeWindowPointerEvent(event);
    };

    /**
     * 处理窗口失焦，避免拖拽会话和 grabbing 光标残留。
     *
     * @param event 窗口失焦事件。
     */
    private readonly handleWindowBlur = (event: Event): void => {
        if (!this.dragSession) {
            return;
        }
        this.finishDragSession(
            this.dragSession.lastClientX,
            this.dragSession.lastClientY,
            false,
        );
    };

    /**
     * 阻止方向箭头拖拽结束后浏览器合成的 click 进入实体选择链。
     *
     * @param event 鼠标点击事件。
     */
    private readonly handleClick = (event: MouseEvent): void => {
        if (!this.shouldSuppressNextClick) {
            return;
        }
        this.shouldSuppressNextClick = false;
        this.consumeEvent(event);
    };

    /**
     * 结束活动拖拽并派发最终 End 状态。
     *
     * @param clientX 结束位置客户端 X 坐标。
     * @param clientY 结束位置客户端 Y 坐标。
     * @param suppressNextClick 是否需要阻止浏览器随后合成的 click。
     */
    private finishDragSession(clientX: number, clientY: number, suppressNextClick: boolean): void {
        if (!this.dragSession) {
            return;
        }
        this.emitDragEvent(
            PatternDirectionArrowAxisDragPhase.End,
            clientX,
            clientY,
            this.dragSession.lastDeltaScene,
        );
        this.dragSession = null;
        this.shouldSuppressNextClick = suppressNextClick;
        this.clearCursor();
    }

    /**
     * 在捕获阶段独占当前方向箭头事件。
     *
     * `stopImmediatePropagation()` 同时阻止同一容器上的其它捕获监听与后续冒泡监听，
     * 确保实体选择和框选状态机不会观察到已被方向箭头认领的鼠标序列。
     *
     * @param event 已由方向箭头认领的浏览器事件。
     */
    private consumeEvent(event: Event): void {
        if (event.cancelable) {
            event.preventDefault();
        }
        event.stopImmediatePropagation();
    }

    /**
     * 在窗口捕获阶段阻止活动指针事件继续进入 Viewer 容器。
     *
     * 窗口上的其它清理监听仍需收到 mouseup / pointercancel，因此这里只停止后续传播，
     * 不使用 `stopImmediatePropagation()` 阻断同级监听。
     *
     * @param event 活动拖拽对应的窗口指针事件。
     */
    private consumeWindowPointerEvent(event: Event): void {
        if (event.cancelable) {
            event.preventDefault();
        }
        event.stopPropagation();
    }

    /**
     * 派发拖拽事件。
     *
     * @param phase 拖拽阶段。
     * @param clientX 事件客户端 X 坐标。
     * @param clientY 事件客户端 Y 坐标。
     * @param deltaScene 场景单位增量。
     */
    private emitDragEvent(
        phase: PatternDirectionArrowAxisDragPhase,
        clientX: number,
        clientY: number,
        deltaScene: number,
    ): void {
        if (!this.dragSession) {
            return;
        }

        // 将场景距离换算成业务参数增量，让监听方不需要理解渲染空间单位。
        const deltaScalar = deltaScene * this.dragSession.sceneUnitToScalarScale;
        const scalarValue = this.dragSession.startScalarValue + deltaScalar;
        this.dragSession.lastDeltaScene = deltaScene;
        this.dragSession.lastScalarValue = scalarValue;
        this.dragSession.lastClientX = clientX;
        this.dragSession.lastClientY = clientY;

        const event: PatternDirectionArrowAxisDragEvent = {
            phase,
            arrow: this.dragSession.arrow,
            contextKey: this.dragSession.contextKey,
            startScalarValue: this.dragSession.startScalarValue,
            scalarValue,
            deltaScene,
            deltaScalar,
            axisOriginWorld: this.dragSession.axisOriginWorld.clone(),
            axisDirectionWorld: this.dragSession.axisDirectionWorld.clone(),
            clientX,
            clientY,
        };
        this.dragSession.arrow.emitAxisDrag(event);
        this.onAxisDrag(event);
    }

    /**
     * 解析当前鼠标命中的可拖拽箭头。
     *
     * @param event 鼠标事件。
     * @returns 命中的箭头对象。
     */
    private resolvePickedArrow(event: MouseEvent): PatternDirectionArrowObject | null {
        const camera = this.viewer.getCamera();
        const container = this.viewer.getContainer();
        if (!camera || !container) {
            return null;
        }

        this.updateMouseNdc(event, container);
        this.raycaster.setFromCamera(this.mouseNdc, camera);
        const hits = this.raycaster.intersectObjects(this.root.children, true);
        for (const hit of hits) {
            const arrow = this.resolveArrowFromObject(hit.object);
            if (arrow?.isAxisDragEnabled()) {
                return arrow;
            }
        }
        return null;
    }

    /**
     * 根据当前鼠标位置更新 hover 光标。
     *
     * @param event 鼠标事件。
     */
    private updateHoverCursor(event: MouseEvent): void {
        const arrow = this.resolvePickedArrow(event);
        if (arrow) {
            this.setGrabCursor();
            return;
        }
        this.clearCursor();
    }

    /**
     * 设置可拖拽光标。
     */
    private setGrabCursor(): void {
        if (this.hasGrabCursor || this.hasGrabbingCursor) {
            return;
        }
        this.viewer.cursorManager.push(ViewportCursorStyle.Grab);
        this.hasGrabCursor = true;
    }

    /**
     * 设置拖拽中光标。
     */
    private setDraggingCursor(): void {
        if (this.hasGrabCursor) {
            this.viewer.cursorManager.pop(ViewportCursorStyle.Grab);
            this.hasGrabCursor = false;
        }
        if (this.hasGrabbingCursor) {
            return;
        }
        this.viewer.cursorManager.push(ViewportCursorStyle.Grabbing);
        this.hasGrabbingCursor = true;
    }

    /**
     * 清理本控制器压入的光标状态。
     */
    private clearCursor(): void {
        if (this.hasGrabbingCursor) {
            this.viewer.cursorManager.pop(ViewportCursorStyle.Grabbing);
            this.hasGrabbingCursor = false;
        }
        if (this.hasGrabCursor) {
            this.viewer.cursorManager.pop(ViewportCursorStyle.Grab);
            this.hasGrabCursor = false;
        }
    }

    /**
     * 从命中对象向父级查找箭头对象。
     *
     * @param object 命中对象。
     * @returns 箭头对象。
     */
    private resolveArrowFromObject(object: THREE.Object3D | null): PatternDirectionArrowObject | null {
        let current: THREE.Object3D | null = object;
        while (current) {
            if (current instanceof PatternDirectionArrowObject) {
                return current;
            }
            if (current === this.root) {
                return null;
            }
            current = current.parent;
        }
        return null;
    }

    /**
     * 根据鼠标事件解析射线投影到拖拽轴的最近点。
     *
     * @param event 鼠标事件。
     * @param axisOrigin 轴原点。
     * @param axisDirection 轴方向。
     * @returns 轴线上最近点。
     */
    private resolvePointOnAxis(
        event: MouseEvent,
        axisOrigin: THREE.Vector3,
        axisDirection: THREE.Vector3,
    ): THREE.Vector3 | null {
        const camera = this.viewer.getCamera();
        const container = this.viewer.getContainer();
        if (!camera || !container) {
            return null;
        }

        this.updateMouseNdc(event, container);
        this.raycaster.setFromCamera(this.mouseNdc, camera);
        return this.closestPointOnAxisFromRay(this.raycaster.ray, axisOrigin, axisDirection);
    }

    /**
     * 更新鼠标标准设备坐标。
     *
     * @param event 鼠标事件。
     * @param container 视口容器。
     */
    private updateMouseNdc(event: MouseEvent, container: HTMLElement): void {
        const rect = container.getBoundingClientRect();
        this.mouseNdc.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mouseNdc.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    }

    /**
     * 归一化轴方向。
     *
     * @param axisDirection 输入方向。
     * @returns 单位方向。
     */
    private normalizeAxis(axisDirection: THREE.Vector3): THREE.Vector3 {
        if (axisDirection.lengthSq() <= 1e-12) {
            return new THREE.Vector3(0, 0, 1);
        }
        return axisDirection.clone().normalize();
    }

    /**
     * 计算射线与轴线的最近点。
     *
     * @param ray 鼠标射线。
     * @param axisOrigin 轴原点。
     * @param axisDirection 轴方向。
     * @returns 轴线上最近点。
     */
    private closestPointOnAxisFromRay(
        ray: THREE.Ray,
        axisOrigin: THREE.Vector3,
        axisDirection: THREE.Vector3,
    ): THREE.Vector3 {
        const axis = this.normalizeAxis(axisDirection);
        const rayDirection = ray.direction.clone().normalize();
        const delta = ray.origin.clone().sub(axisOrigin);
        const a = rayDirection.dot(rayDirection);
        const b = rayDirection.dot(axis);
        const c = axis.dot(axis);
        const d = rayDirection.dot(delta);
        const e = axis.dot(delta);
        const denominator = a * c - b * b;

        // 射线与轴接近平行时退回到轴向投影，避免除零导致拖拽跳变。
        const axisParam = Math.abs(denominator) > 1e-9
            ? (a * e - b * d) / denominator
            : e / c;

        return axisOrigin.clone().addScaledVector(axis, axisParam);
    }
}

